/**
 * Sidebar Navigation Component
 */

import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import './Sidebar.css';

const Sidebar = () => {
  const { user, logout, isOwner, isSuperuser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const isActive = (path) => location.pathname === path;

  return (
    <>
      {/* Mobile toggle button */}
      <button
        className="sidebar-toggle"
        onClick={() => setMobileOpen(!mobileOpen)}
        aria-label="Menu"
      >
        {mobileOpen ? '\u2715' : '\u2630'}
      </button>

      {/* Overlay for mobile */}
      {mobileOpen && (
        <div className="sidebar-overlay" onClick={() => setMobileOpen(false)} />
      )}

      <aside className={`sidebar ${mobileOpen ? 'open' : ''}`}>
        <nav className="sidebar-nav">
          <div className="nav-section">
            <span className="nav-section-label">TRAINING</span>
            <Link
              to="/"
              className={`nav-item ${isActive('/') ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                <line x1="16" y1="2" x2="16" y2="6" />
                <line x1="8" y1="2" x2="8" y2="6" />
                <line x1="3" y1="10" x2="21" y2="10" />
              </svg>
              Training buchen
            </Link>
            <Link
              to="/my-bookings"
              className={`nav-item ${isActive('/my-bookings') ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
              Meine Buchungen
            </Link>
            <Link
              to="/faq"
              className={`nav-item ${isActive('/faq') ? 'active' : ''}`}
              onClick={() => setMobileOpen(false)}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="12" cy="12" r="10" />
                <path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3" />
                <line x1="12" y1="17" x2="12.01" y2="17" />
              </svg>
              FAQ
            </Link>
          </div>

          {isOwner() && (
            <>
              <div className="nav-section">
                <span className="nav-section-label">BETRIEB</span>
                <Link to="/owner" className={`nav-item nav-sub ${isActive('/owner') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Übersicht</Link>
                <Link to="/owner/leads" className={`nav-item nav-sub ${isActive('/owner/leads') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Anfragen</Link>
                <Link to="/owner/tasks" className={`nav-item nav-sub ${isActive('/owner/tasks') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Aufgaben</Link>
                <Link to="/owner/customers" className={`nav-item nav-sub ${isActive('/owner/customers') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Kunden</Link>
                <Link to="/owner/bookings" className={`nav-item nav-sub ${isActive('/owner/bookings') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Buchungen</Link>
                <Link to="/owner/campaigns" className={`nav-item nav-sub ${isActive('/owner/campaigns') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>E-Mail Kampagnen</Link>
              </div>
              <div className="nav-section">
                <span className="nav-section-label">ANALYSE</span>
                <Link to="/owner/stats" className={`nav-item nav-sub ${isActive('/owner/stats') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Statistiken</Link>
                <Link to="/owner/revenue" className={`nav-item nav-sub ${isActive('/owner/revenue') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Umsatz</Link>
                <Link to="/owner/log" className={`nav-item nav-sub ${isActive('/owner/log') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Buchungs-Log</Link>
                <Link to="/owner/nuki" className={`nav-item nav-sub ${isActive('/owner/nuki') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Nuki Schloss</Link>
              </div>
              <div className="nav-section">
                <span className="nav-section-label">VERWALTUNG</span>
                <Link to="/owner/studios" className={`nav-item nav-sub ${isActive('/owner/studios') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Studios</Link>
                <Link to="/owner/settings" className={`nav-item nav-sub ${isActive('/owner/settings') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Einstellungen</Link>
                <Link to="/owner/website" className={`nav-item nav-sub ${isActive('/owner/website') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Website</Link>
              </div>
              <div className="nav-section">
                <span className="nav-section-label">SYSTEM</span>
                <Link to="/owner/import" className={`nav-item nav-sub ${isActive('/owner/import') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>CSV Import</Link>
                <Link to="/owner/backups" className={`nav-item nav-sub ${isActive('/owner/backups') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Backups</Link>
              </div>
            </>
          )}

          {!isOwner() && isSuperuser() && (
            <div className="nav-section">
              <span className="nav-section-label">SUPERUSER</span>
              <Link to="/owner/log" className={`nav-item nav-sub ${isActive('/owner/log') ? 'active' : ''}`} onClick={() => setMobileOpen(false)}>Buchungs-Log</Link>
            </div>
          )}

        </nav>

        <div className="sidebar-footer">
          <div className="user-info">
            <div className="user-avatar">
              {user?.name?.charAt(0)?.toUpperCase() || 'U'}
            </div>
            <div className="user-details">
              <span className="user-name">{user?.name}</span>
              <span className="user-role">{user?.role || 'Client'}{user?.studioName ? ` · ${user.studioName}` : ''}</span>
            </div>
          </div>
          <button onClick={handleLogout} className="logout-btn" title="Logout">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
              <polyline points="16 17 21 12 16 7" />
              <line x1="21" y1="12" x2="9" y2="12" />
            </svg>
          </button>
        </div>
      </aside>
    </>
  );
};

export default Sidebar;
